import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { EmailPreview } from '@/components/email/EmailPreview';

// Pre-send confirmation modals for the admin invite / resend flows. The email body is the BE
// single-owner render (GET /admin/email-previews/admin-invite), personalized with the entered form
// values so the operator sees the exact mail that will be sent — NOT a hand-built second template.
// The shared body is exported for the resend variant to reuse.

const I18N = 'adminManagement.invitePreviewModal';

interface AdminInvitePreviewBodyProps {
  name: string;
  email: string;
  // Comma-joined role names, exactly as shown elsewhere in the admin UI; passed to the render as-is.
  rolesLabel: string;
  // Send-time chrome string (YYYY-MM-DD HH:mm), built by the caller.
  sentAt: string;
  // Renders the resend banner (server-side) instead of a first-time invite.
  resend?: boolean;
}

/** The email-body preview shared by the admin invite and resend modals — the BE render, not a copy. */
export function AdminInvitePreviewBody({
  name,
  email,
  rolesLabel,
  sentAt,
  resend = false,
}: AdminInvitePreviewBodyProps) {
  // SHCT-651 — no FE-side 발신자/발송일시/받는사람 box: `_layout.html :: page` already renders it at
  // the top of every mail, so a second copy here put the header on screen twice. The entered values
  // ride along as vars instead.
  return (
    <EmailPreview
      kind="admin-invite"
      resend={resend}
      vars={{ name, role: rolesLabel, recipientEmail: email, sentAt }}
    />
  );
}

interface AdminInvitePreviewModalProps {
  open: boolean;
  name: string;
  email: string;
  rolesLabel: string;
  sentAt: string;
  submitting: boolean;
  // [← 수정] — close the preview and return to the form.
  onEdit: () => void;
  // [✉ 초대 발송] — run the actual invite.
  onConfirm: () => void;
}

export function AdminInvitePreviewModal({
  open,
  name,
  email,
  rolesLabel,
  sentAt,
  submitting,
  onEdit,
  onConfirm,
}: AdminInvitePreviewModalProps) {
  const { t } = useTranslation('admin');

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onEdit]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-overlay modal-overlay--visible"
      onClick={onEdit}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="modal"
        style={{
          maxWidth: 640,
          width: '92vw',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '88vh',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 className="modal__title">{t(`${I18N}.title`)}</h2>
        </div>
        <div className="modal__body" style={{ overflowY: 'auto' }}>
          <AdminInvitePreviewBody
            name={name}
            email={email}
            rolesLabel={rolesLabel}
            sentAt={sentAt}
          />
        </div>
        <div className="modal__footer">
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={onEdit}
            disabled={submitting}
          >
            {t(`${I18N}.editButton`)}
          </button>
          <button
            type="button"
            className="btn btn-default btn-primary"
            onClick={onConfirm}
            disabled={submitting}
          >
            {t(`${I18N}.sendButton`)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
